import { useRBAC } from "@bcc/contexts/rbac-context";
import type { AccessibleModule, PageAction } from "@shared/types";
/**
 * Hook to check specific page/action permissions
 * Provides convenient methods for checking permissions in components
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { canView, canCreate, canSeeModule } = usePermissions();
 *
 *   return (
 *     <div>
 *       {canSeeModule("orders") && <OrdersNav />}
 *       {canView("orders") && <OrdersList />}
 *       {canCreate("orders") && <CreateOrderButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissions() {
  const { hasPageAccess, hasModuleAccess, hasAction, getModuleActions, isLoading, featureName, permissions } = useRBAC();

  return {
    /**
     * Check if user can view a specific module
     * @param module - The module name (e.g., "orders", "batch")
     */
    canView: (module: AccessibleModule) => hasAction(module, "view"),

    /**
     * Check if user can create in a module
     * @param module - The module name (e.g., "orders", "batch")
     */
    canCreate: (module: AccessibleModule) => hasAction(module, "create"),

    /**
     * Check if user can edit in a module
     * @param module - The module name (e.g., "products")
     */
    canEdit: (module: AccessibleModule) => hasAction(module, "edit"),

    /**
     * Check if user can delete in a module
     * @param module - The module name
     */
    canDelete: (module: AccessibleModule) => hasAction(module, "delete"),

    /**
     * Check if user can export from a module
     * @param module - The module name (e.g., "orders", "dashboard")
     */
    canExport: (module: AccessibleModule) => hasAction(module, "export"),

    /**
     * Check if user can print in a module
     * @param module - The module name (e.g., "orders", "batch", "labels")
     */
    canPrint: (module: AccessibleModule) => hasAction(module, "print"),

    /**
     * Check if user can sync in a module
     * @param module - The module name (e.g., "products")
     */
    canSync: (module: AccessibleModule) => hasAction(module, "sync"),

    /**
     * Check full page access (e.g., "orders.view", "batch.create")
     * @param page - The full page permission string
     */
    canAccessPage: (page: string) => hasPageAccess(page),

    /**
     * Check module visibility in menu
     * @param module - The module name (e.g., "orders")
     */
    canSeeModule: (module: AccessibleModule) => hasModuleAccess(module),

    /**
     * Check if user has a specific action on a module
     * @param module - The module name
     * @param action - The action type
     */
    hasAction: (module: AccessibleModule, action: PageAction) => hasAction(module, action),

    /**
     * Get all available actions for a module
     * @param module - The module name
     */
    getActions: (module: AccessibleModule) => getModuleActions(module),

    /**
     * Current feature name (BLP, SS, BC OG)
     */
    featureName,

    /**
     * Raw permissions object
     */
    permissions,

    /**
     * Loading state - true while permissions are being fetched
     */
    isLoading,
  };
}
